<script setup>
import { ref, watch } from "vue";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";

const props = defineProps({
  // nilai
  modelValue: {
    type: [Array, Object, String, Number, Boolean, null],
    default: null,
  },

  // sumber opsi lokal
  options: { type: Array, default: () => [] },

  // mode & UX
  multiple: { type: Boolean, default: false },
  searchable: { type: Boolean, default: true },
  placeholder: { type: String, default: "Select…" },
  disabled: { type: Boolean, default: false },
  clearOnSelect: { type: Boolean, default: false },
  closeOnSelect: { type: Boolean, default: true },
  maxHeight: { type: Number, default: 300 },
  taggable: { type: Boolean, default: false },
  allowEmpty: { type: Boolean, default: true },
  showLabels: { type: Boolean, default: true },
  selectLabel: { type: String, default: "Click for select" },
  deselectLabel: { type: String, default: "Click for delete" },
  selectedLabel: { type: String, default: "Selected" },

  // mapping field
  labelKey: { type: String, default: "label" },
  trackBy: { type: String, default: "value" },

  // =====================
  // Mode pencarian remote (API)
  // =====================
  remote: { type: Boolean, default: false }, // aktifkan untuk hit API
  minChars: { type: Number, default: 2 }, // minimal karakter sebelum fetch
  debounce: { type: Number, default: 300 }, // ms
  cache: { type: Boolean, default: true },

  groupValues: { type: String, default: "" },
  groupLabel: { type: String, default: "" },
  groupSelect: { type: Boolean, default: false },

  // Cara fetch: sediakan salah satu
  remoteUrl: { type: [String, Function], default: "" }, // string atau (q)=>string
  remoteFetch: { type: Function, default: null }, // (q)=>Promise<Array>
  remoteMethod: { type: String, default: "GET" },
  remoteQueryParam: { type: String, default: "q" }, // nama param pencarian
  remoteHeaders: { type: Object, default: () => ({}) },
  remoteBodyTemplate: { type: Function, default: (q) => ({ q }) },

  // Transform hasil API → array opsi
  mapResponse: {
    type: Function,
    default: (data) =>
      Array.isArray(data) ? data : data?.items || data?.data || [],
  },
});

const emit = defineEmits([
  "update:modelValue",
  "change",
  "search-change",
  "fetch-start",
  "fetch-end",
  "fetch-error",
]);

// v-model bridge
const inner = ref(props.modelValue);
watch(
  () => props.modelValue,
  (v) => (inner.value = v),
);
watch(inner, (v) => emit("update:modelValue", v));

// opsi yang ditampilkan
const remoteOptions = ref([]);
const loading = ref(false);
let timer = null;
let aborter = null;
const memo = new Map();

function doDebounce(fn, wait) {
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

async function fetchRemote(q) {
  if (props.minChars && (!q || q.length < props.minChars)) {
    remoteOptions.value = [];
    return;
  }

  const key = q || "";
  if (props.cache && memo.has(key)) {
    remoteOptions.value = memo.get(key);
    return;
  }

  // cancel request sebelumnya
  try {
    aborter?.abort?.();
  } catch {}
  aborter = new AbortController();

  try {
    loading.value = true;
    emit("fetch-start", q);

    let items;
    if (typeof props.remoteFetch === "function") {
      items = await props.remoteFetch(q);
    } else {
      // rakit request dari remoteUrl
      const base =
        typeof props.remoteUrl === "function"
          ? props.remoteUrl(q)
          : props.remoteUrl;
      let url = base || "";
      const init = {
        method: props.remoteMethod || "GET",
        headers: { "Content-Type": "application/json", ...props.remoteHeaders },
        signal: aborter.signal,
      };

      if ((props.remoteMethod || "GET").toUpperCase() === "GET") {
        const u = new URL(url, window.location.origin);
        u.searchParams.set(props.remoteQueryParam, q || "");
        url = u.toString();
      } else {
        init.body = JSON.stringify(props.remoteBodyTemplate(q));
      }

      const res = await fetch(url, init);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      items = props.mapResponse(data);
    }

    remoteOptions.value = Array.isArray(items) ? items : [];
    if (props.cache) memo.set(key, remoteOptions.value);
    emit("fetch-end", q);
  } catch (err) {
    if (err?.name === "AbortError") return;
    emit("fetch-error", err);
    console.error("[AppMultiselect] fetch error:", err);
  } finally {
    loading.value = false;
  }
}

const debouncedFetch = doDebounce(fetchRemote, props.debounce);

function onSearch(q) {
  emit("search-change", q);
  if (props.remote) debouncedFetch(q);
}

function onChange(val) {
  emit("change", val);
}
</script>

<template>
  <Multiselect
    v-model="inner"
    :options="remote ? remoteOptions : options"
    :multiple="multiple"
    :label="labelKey"
    :track-by="trackBy"
    :placeholder="placeholder"
    :searchable="searchable"
    :internal-search="!remote"
    :clear-on-select="clearOnSelect"
    :close-on-select="closeOnSelect"
    :disabled="disabled"
    :max-height="maxHeight"
    :taggable="taggable"
    :allow-empty="allowEmpty"
    :show-labels="showLabels"
    :select-label="selectLabel"
    :deselect-label="deselectLabel"
    :selected-label="selectedLabel"
    :loading="loading"
    :group-values="groupValues"
    :group-label="groupLabel"
    :group-select="groupSelect"
    @change="onChange"
    @search-change="onSearch"
  />
</template>

<style>
/* Override styling di sini bila perlu */
</style>
